/**
 * Product Owner Registration Page
 * @implements TS-016 - Product Owner Registration UI
 * @implements FS-001.1.1 - Product Owner Registration
 * @implements BS-001.1 - Invitation Code Management
 *
 * This page handles Product Owner registration after OAuth authentication.
 * It reads the OAuth profile from the temp_profile cookie and collects
 * the invitation code required for PO registration.
 *
 * Flow:
 * 1. User clicks "Register as Product Owner" and authenticates via OAuth
 * 2. Backend sets temp_profile cookie with OAuth data
 * 3. Backend redirects to /register/product-owner
 * 4. This page reads the cookie and shows registration form
 * 5. User enters invitation code and submits
 * 6. On success, user is redirected to project creation
 */

import { useState, useEffect, FormEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useTempProfile } from '@/hooks/useTempProfile';
import { useAuthContext } from '@/providers';
import { GitHubOAuthButton, GoogleOAuthButton } from '@/components/auth';

/**
 * Validate invitation code format
 * @implements BS-001.1 - Invitation Code Management
 * @param code The invitation code to validate
 * @returns Error message if invalid, null if valid
 */
function validateInvitationCode(code: string): string | null {
  if (!code.trim()) {
    return 'Invitation code is required';
  }
  // Basic format validation - codes should be alphanumeric with optional dashes
  // Format: XXXX-XXXX-XXXX or similar
  const cleanCode = code.replace(/-/g, '');
  if (cleanCode.length < 8) {
    return 'Invitation code is too short';
  }
  if (!/^[A-Z0-9]+$/.test(cleanCode)) {
    return 'Invitation code should contain only letters and numbers';
  }
  return null;
}

/**
 * Session Expired View
 * Shown when no temp_profile cookie is found
 */
const SessionExpiredView = ({ error }: { error: string | null }) => (
  <Container maxWidth="sm">
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Session Expired
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {error ||
            'Your registration session has expired. Please start again.'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <GitHubOAuthButton flow="register_po" />
          <GoogleOAuthButton flow="register_po" />
        </Box>
        <Box sx={{ mt: 3 }}>
          <Button
            component={RouterLink}
            to="/register"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Back to registration options
          </Button>
        </Box>
      </Paper>
    </Box>
  </Container>
);

/**
 * Loading View
 * Shown while reading the temp_profile cookie
 */
const LoadingView = () => (
  <Container maxWidth="sm">
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <CircularProgress size={48} />
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
        Loading your profile...
      </Typography>
    </Box>
  </Container>
);

/**
 * Product Owner Registration Page Component
 * @implements TS-016 - Product Owner Registration UI
 * @implements FS-001.1.1 - Product Owner Registration
 * @implements BS-001.1 - Invitation Code Management
 */
export const RegisterProductOwnerPage = () => {
  // Read OAuth profile from temp_profile cookie
  const {
    profile: oauthProfile,
    isLoading: profileLoading,
    error: profileError,
    clearProfile,
  } = useTempProfile();

  // Auth context for registration mutation
  const { registerProductOwner, isRegistering, registerError } =
    useAuthContext();

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [invitationCodeError, setInvitationCodeError] = useState<string | null>(
    null,
  );

  // Pre-fill display name from OAuth profile
  useEffect(() => {
    if (oauthProfile?.name) {
      setDisplayName(oauthProfile.name);
    }
  }, [oauthProfile]);

  // Show loading state while reading cookie
  if (profileLoading) {
    return <LoadingView />;
  }

  // Show session expired if no profile found
  if (!oauthProfile) {
    return <SessionExpiredView error={profileError} />;
  }

  /**
   * Handle invitation code change with validation
   * @implements BS-001.1 - Invitation Code Management
   */
  const handleInvitationCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value.toUpperCase();
    setInvitationCode(value);
    // Clear validation error when user types
    if (invitationCodeError) {
      setInvitationCodeError(null);
    }
  };

  /**
   * Handle form submission
   * @implements FS-001.1.1 - Product Owner Registration
   * @implements BS-001.1 - Invitation Code Management
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setInvitationCodeError(null);

    // Validate invitation code format
    const codeError = validateInvitationCode(invitationCode);
    if (codeError) {
      setInvitationCodeError(codeError);
      return;
    }

    try {
      // Call registration mutation via AuthContext
      // Only send invitationCode and displayName - email comes from oauth_pending cookie on backend
      const redirectTo = await registerProductOwner({
        invitationCode: invitationCode.trim(),
        displayName: displayName.trim() || undefined,
      });

      if (redirectTo) {
        // Clear the temp_profile cookie after successful registration
        clearProfile();
        // Redirect to the project creation page
        window.location.href = redirectTo;
      }
    } catch (err) {
      // Handle registration errors
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Registration failed. Please try again.';
      setFormError(errorMessage);
    }
  };

  // Combined error from form validation or mutation
  const displayError = formError || registerError?.message;

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Register as Product Owner
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 4 }}
          >
            Complete your registration to start creating projects
          </Typography>

          {/* OAuth Profile Summary */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
              p: 2,
              backgroundColor: 'grey.50',
              borderRadius: 1,
            }}
          >
            {oauthProfile.avatarUrl && (
              <Avatar
                src={oauthProfile.avatarUrl}
                alt={oauthProfile.name}
                sx={{ width: 48, height: 48 }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">
                Signed in with{' '}
                {oauthProfile.provider === 'github' ? 'GitHub' : 'Google'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {oauthProfile.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Registration Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {/* Email (read-only from OAuth) */}
            <TextField
              fullWidth
              label="Email"
              value={oauthProfile.email}
              disabled
              sx={{ mb: 3 }}
              helperText={`From your ${
                oauthProfile.provider === 'github' ? 'GitHub' : 'Google'
              } account`}
            />

            {/* Display Name (editable) */}
            <TextField
              fullWidth
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              sx={{ mb: 3 }}
              helperText="You can change how your name appears in the app"
            />

            {/* Invitation Code (required) */}
            <TextField
              fullWidth
              label="Invitation Code"
              value={invitationCode}
              onChange={handleInvitationCodeChange}
              required
              error={!!invitationCodeError}
              helperText={
                invitationCodeError || 'Enter the invitation code you received'
              }
              placeholder="XXXX-XXXX-XXXX"
              sx={{ mb: 3 }}
              inputProps={{
                style: { textTransform: 'uppercase' },
              }}
            />

            {/* Error Alert */}
            {displayError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {displayError}
              </Alert>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isRegistering}
              sx={{ mb: 2 }}
            >
              {isRegistering ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            {/* Cancel Link */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                component={RouterLink}
                to="/register"
                color="primary"
                sx={{ textTransform: 'none' }}
              >
                Cancel and choose a different role
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
